/* UTM / ad / source tracking helpers.
 *
 * On every page load:
 *  1. Read tracking params from the URL (utm_*, fbclid, gclid, ref, src).
 *  2. Persist a "first-touch" record in localStorage so we keep the original
 *     ad source even if the merchant browses around before submitting.
 *  3. Overwrite a "last-touch" record on every visit with tracking info.
 *  4. Strip ONLY the known tracking params from the visible URL so the merchant
 *     sees a clean address (e.g. /free-pos-offer?utm_source=facebook → /free-pos-offer).
 *
 * When a lead form submits, call getTrackingPayload() and include it in the POST.
 * Backend treats every field as optional — anything missing falls back to "direct/unknown".
 */

const TRACK_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "fbclid", "gclid", "ref", "src"];
const FIRST_KEY = "swipe_first_touch";
const LAST_KEY = "swipe_last_touch";

/* Canonical maps — must stay in sync with backend SOURCE_LABELS / CAMPAIGN_LABELS in server.py. */
const SOURCE_MAP = {
  facebook: "Facebook", fb: "Facebook", meta: "Facebook",
  instagram: "Instagram", ig: "Instagram",
  tiktok: "TikTok", tik_tok: "TikTok",
  google: "Google", google_organic: "Google",
  google_ads: "Google Ads", googleads: "Google Ads", adwords: "Google Ads",
  google_business_profile: "Google Business Profile", gmb: "Google Business Profile",
  google_business: "Google Business Profile", gbp: "Google Business Profile",
  youtube: "YouTube", yt: "YouTube",
  linkedin: "LinkedIn", li: "LinkedIn",
  x: "X / Twitter", twitter: "X / Twitter", x_twitter: "X / Twitter",
  yelp: "Yelp",
  referral: "Referral", referrer: "Referral",
  direct: "Website Direct", website: "Website Direct", website_direct: "Website Direct",
};
const CAMPAIGN_MAP = {
  cash_discount: "Cash Discount", cashdiscount: "Cash Discount",
  cash_discount_savings: "Cash Discount", cash_discount_promotion: "Cash Discount",
  free_pos: "Free POS", freepos: "Free POS",
  free_pos_program: "Free POS", free_pos_offer: "Free POS",
  savings_analysis: "Savings Analysis", savingsanalysis: "Savings Analysis", savings: "Savings Analysis",
};
const norm = (s) => (s || "").toString().trim().toLowerCase().replace(/[\s.-]+/g, "_");
const titleCase = (s) => s.replace(/[_-]+/g, " ").split(/\s+/).map((w) => w[0]?.toUpperCase() + w.slice(1).toLowerCase()).join(" ");
function prettySource(utmSource, utmMedium) {
  if (!utmSource) return null;
  const label = SOURCE_MAP[norm(utmSource)];
  if (label) {
    const med = norm(utmMedium);
    if (label === "Google" && ["cpc", "ppc", "paid_search", "paid"].includes(med)) return "Google Ads";
    return label;
  }
  return titleCase(utmSource);
}
function prettyCampaign(utmCampaign) {
  if (!utmCampaign) return null;
  return CAMPAIGN_MAP[norm(utmCampaign)] || titleCase(utmCampaign);
}

function safeReadJSON(key) {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function safeWriteJSON(key, value) {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(key, JSON.stringify(value)); }
  catch (err) { console.warn("[utm] localStorage write failed:", err); }
}

/* Build a single-word "source" label for emails/digest from a touch record. */
function summariseSource(touch) {
  if (!touch) return "Direct / Unknown";
  const src = prettySource(touch.utm_source, touch.utm_medium);
  const camp = prettyCampaign(touch.utm_campaign);
  if (src && camp) return `${src} · ${camp}`;
  if (src) return src;
  if (touch.fbclid) return "Facebook Ads (fbclid)";
  if (touch.gclid) return "Google Ads (gclid)";
  if (touch.ref) return `Ref: ${touch.ref}`;
  if (touch.src) return `Src: ${touch.src}`;
  if (touch.referrer_url) {
    try { return `Referrer: ${new URL(touch.referrer_url).hostname}`; }
    catch { return "Referrer"; }
  }
  return "Direct / Unknown";
}

/* Run once at app boot. Idempotent — safe to call repeatedly. */
export function captureAndCleanUrl() {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  const params = url.searchParams;
  const captured = {};
  let touched = false;
  for (const key of TRACK_PARAMS) {
    const v = params.get(key);
    if (v) {
      captured[key] = v;
      touched = true;
    }
  }
  const now = new Date().toISOString();
  const landing = `${url.origin}${url.pathname}`;
  const referrer = (typeof document !== "undefined" && document.referrer) ? document.referrer : "";

  // If nothing was captured this visit, do not overwrite an existing last_touch — keep
  // the prior tracking record so React StrictMode double-fires don't blank it out.
  if (touched) {
    const lastTouch = {
      ...captured,
      landing_page_url: landing,
      referrer_url: referrer,
      captured_at: now,
    };
    lastTouch.summary = summariseSource(lastTouch);
    safeWriteJSON(LAST_KEY, lastTouch);

    const existingFirst = safeReadJSON(FIRST_KEY);
    if (!existingFirst) safeWriteJSON(FIRST_KEY, lastTouch);
  } else if (!safeReadJSON(LAST_KEY)) {
    // First-ever visit with no tracking — record a minimal "direct" last_touch
    // so submissions still attribute correctly.
    const directTouch = {
      landing_page_url: landing,
      referrer_url: referrer,
      captured_at: now,
      summary: summariseSource({ referrer_url: referrer }),
    };
    safeWriteJSON(LAST_KEY, directTouch);
  }

  // Strip ONLY known tracking params from the visible URL.
  if (touched) {
    for (const key of TRACK_PARAMS) params.delete(key);
    const clean = url.pathname + (params.toString() ? `?${params.toString()}` : "") + url.hash;
    try { window.history.replaceState({}, "", clean); }
    catch (err) { console.warn("[utm] history.replaceState failed:", err); }
  }
}

/* Read current tracking state and return the payload to attach to a form submission.
 * All fields optional; backend persists whatever it gets. */
export function getTrackingPayload() {
  const first = safeReadJSON(FIRST_KEY) || {};
  const last = safeReadJSON(LAST_KEY) || {};
  return {
    utm_source: last.utm_source || null,
    utm_medium: last.utm_medium || null,
    utm_campaign: last.utm_campaign || null,
    utm_content: last.utm_content || null,
    utm_term: last.utm_term || null,
    fbclid: last.fbclid || null,
    gclid: last.gclid || null,
    landing_page_url: last.landing_page_url || (typeof window !== "undefined" ? window.location.href : null),
    referrer_url: last.referrer_url || (typeof document !== "undefined" ? document.referrer || null : null),
    first_touch_source: first.summary || summariseSource(first),
    last_touch_source: last.summary || summariseSource(last),
  };
}

/* Convenience for tests / admin views. */
export function readTrackingState() {
  return { first: safeReadJSON(FIRST_KEY), last: safeReadJSON(LAST_KEY) };
}
